import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  GraduationCap,
  LoaderCircle,
  Moon,
  School,
  ShieldCheck,
  Sparkles,
  Sun,
  UsersRound,
} from "lucide-react";
import { doc, getDocFromServer } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { ensureTeacherClaim } from "./utils/teacherClaim";
import { buildAccountState, isOnboardingComplete } from "./utils/accountProfile";
import { clearPendingUsername, readPendingUsername, validUsername } from "./utils/pendingUsername";
import "./OnboardingScreen.css";
import StudyDenLogo from "./assets/images/branding/Studyden_Horizontal.svg";
import { API_BASE_URL } from "./utils/apiBase";


const OnboardingScreen = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // "teacher" | "student" | "account" | null
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // Set only when the backend reports that no registration username was saved.
  const [needsUsername, setNeedsUsername] = useState(false);
  const [recoveryUsername, setRecoveryUsername] = useState("");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    const syncTheme = () => {
      const nextMode = localStorage.getItem("darkMode") === "true";
      setDarkMode(nextMode);
      document.body.classList.toggle("dark-mode", nextMode);
      document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const toggleTheme = () => {
    const nextMode = !darkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  // Set when the backend says this account finished onboarding but its
  // profile couldn't then be loaded; retrying reloads the profile instead of
  // repeating the onboarding request.
  const [accountReloadNeeded, setAccountReloadNeeded] = useState(false);

  // The backend found onboarding already complete, so this screen was shown
  // from an out-of-date view of the account. Load the authoritative profile
  // and route by it; never re-submit a role.
  const recoverCompletedAccount = async () => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      setError("You are not signed in. Please sign in and try again.");
      return;
    }
    setError("");
    setLoading("account");
    try {
      const profile = await getDocFromServer(doc(db, "users", currentUser.uid));
      const userData = profile.exists() ? profile.data() : null;
      if (!userData || !isOnboardingComplete(userData)) {
        setAccountReloadNeeded(false);
        setError("Your account setup couldn't be confirmed. Please contact StudyDen support.");
        setLoading(null);
        return;
      }
      if (userData.role === "teacher") await ensureTeacherClaim(currentUser);
      setAccountReloadNeeded(false);
      if (setUser) setUser(buildAccountState(currentUser, userData));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Could not load the completed account:", err?.code || err?.message);
      setAccountReloadNeeded(true);
      setError("Your account is already set up, but StudyDen couldn't load it. Check your connection and try again.");
      setLoading(null);
    }
  };

  const handleSelect = async (selectedRole) => {
    setError("");
    setSuccessMessage("");
    setLoading(selectedRole);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        setError("You are not signed in. Please sign in and try again.");
        setLoading(null);
        return;
      }

      // The username chosen at signup, in case the profile write hasn't landed;
      // the backend uses it only to create a missing profile.
      const username = needsUsername ? validUsername(recoveryUsername) : readPendingUsername(currentUser.uid);
      if (needsUsername && !username) {
        setError("Enter the username you chose when you registered (up to 100 characters).");
        setLoading(null);
        return;
      }

      const token = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/api/complete-onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(username ? { selectedRole, username } : { selectedRole }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        // ignore parse errors, handle below
      }

      if (!response.ok || data.ok === false) {
        // The backend's reply when this account's profile already records
        // onboardingCompleted.
        if (data.error === "Onboarding already completed.") {
          await recoverCompletedAccount();
          return;
        }
        if (data.code === "username-required") setNeedsUsername(true);
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(null);
        return;
      }
      clearPendingUsername(currentUser.uid);

      // The server has just added the teacher claim; refresh the token so the
      // dashboard's Firestore reads carry it.
      if (selectedRole === "teacher") await ensureTeacherClaim(currentUser);

      // Update local user state so routing guards immediately see the new role/status
      if (setUser) {
        setUser((prev) => ({
          ...prev,
          role: selectedRole,
          onboardingCompleted: true,
        }));
      }

      if (selectedRole === "teacher") {
        setSuccessMessage(
          "Your 4-week teacher trial is ready. You have 20 credits to get started."
        );
        // Brief pause so the user sees the success message before navigating
        setTimeout(() => navigate("/dashboard"), 1800);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      setError(
        "A network error occurred. Please check your connection and try again."
      );
      setLoading(null);
    }
  };

  return (
    <div className={"onboarding-page" + (darkMode ? " onboarding-dark" : "")}>
      <div className="onboarding-backdrop" aria-hidden="true">
        <span className="onboarding-shape onboarding-shape-one" />
        <span className="onboarding-shape onboarding-shape-two" />
        <span className="onboarding-shape onboarding-shape-three" />
      </div>

      <header className="onboarding-topbar">
        <div className="onboarding-brand-plaque">
          <img src={StudyDenLogo} alt="StudyDen — Learn. Assess. Progress." />
        </div>
        <button type="button" className="onboarding-theme-toggle" onClick={toggleTheme}
          aria-label={darkMode ? "Use light mode" : "Use dark mode"}>
          {darkMode ? <Sun size={19} /> : <Moon size={19} />}
        </button>
      </header>

      <main className="onboarding-inner">
        <section className="onboarding-hero">
          <div className="onboarding-hero-copy">
            <p className="onboarding-kicker">Welcome to StudyDen</p>
            <h1>Let’s get you set up</h1>
            <p>Choose how you’ll use StudyDen so we can take you to the right workspace.</p>
          </div>
          <div className="onboarding-hero-art" aria-hidden="true">
            <span><Sparkles size={31} /></span>
            <i className="onboarding-orbit onboarding-orbit-one"><School size={17} /></i>
            <i className="onboarding-orbit onboarding-orbit-two"><BookOpenCheck size={18} /></i>
            <i className="onboarding-orbit onboarding-orbit-three"><UsersRound size={17} /></i>
          </div>
        </section>

        <section className="onboarding-step-card" aria-labelledby="onboarding-role-heading">
          <div className="onboarding-step-heading">
            <div>
              <p>Account setup</p>
              <h2 id="onboarding-role-heading">Choose your account type</h2>
              <span>Select the option that best describes how you’ll use StudyDen.</span>
            </div>
            <span className="onboarding-step-badge">One quick step</span>
          </div>

          {error && (
            <div className="onboarding-error-banner" role="alert">
              <AlertTriangle size={19} aria-hidden="true" /><span>{error}</span>
            </div>
          )}
          {accountReloadNeeded && (
            <button className="onboarding-btn student-btn" type="button"
              onClick={recoverCompletedAccount} disabled={loading !== null}>
              {loading === "account" ? "Loading your account…" : "Try again"}
            </button>
          )}

          {successMessage && (
            <div className="onboarding-success-banner" role="status">
              <CheckCircle2 size={19} aria-hidden="true" /><span>{successMessage}</span>
            </div>
          )}

          {needsUsername && (
            <div className="onboarding-username-field">
              <label htmlFor="onboarding-username">Username</label>
              <input
                id="onboarding-username"
                type="text"
                value={recoveryUsername}
                onChange={(event) => setRecoveryUsername(event.target.value)}
                placeholder="The username you chose when registering"
                autoComplete="username"
                maxLength={100}
                required
                disabled={loading !== null}
              />
              <span>Then choose your account type below to finish setting up.</span>
            </div>
          )}

          <div className="onboarding-cards">
            <article className={"onboarding-card onboarding-teacher-card" + (loading === "teacher" ? " is-selected" : "")}
              aria-busy={loading === "teacher"}>
              {loading === "teacher" && <span className="onboarding-selected-mark" aria-hidden="true"><CheckCircle2 size={17} /></span>}
              <div className="onboarding-card-icon"><GraduationCap size={27} /></div>
              <p className="onboarding-card-eyebrow">For educators</p>
              <h3 className="onboarding-card-title">I’m a Teacher</h3>
              <p className="onboarding-card-subtitle">
                Create tests, mark student work, generate feedback, and track progress.
              </p>
              <ul className="onboarding-features">
                <li><CheckCircle2 size={15} />AI test generation</li>
                <li><CheckCircle2 size={15} />Automated marking</li>
                <li><CheckCircle2 size={15} />Paper submission marking</li>
                <li><CheckCircle2 size={15} />Student reports and analytics</li>
              </ul>
              <button className="onboarding-btn teacher-btn" type="button"
                onClick={() => handleSelect("teacher")} disabled={loading !== null}
                aria-label="Start free teacher trial">
                {loading === "teacher"
                  ? <><LoaderCircle className="onboarding-spinner" size={18} aria-hidden="true" />Setting up...</>
                  : <>Start Free Teacher Trial<ArrowRight size={17} /></>}
              </button>
            </article>

            <article className={"onboarding-card onboarding-student-card" + (loading === "student" ? " is-selected" : "")}
              aria-busy={loading === "student"}>
              {loading === "student" && <span className="onboarding-selected-mark" aria-hidden="true"><CheckCircle2 size={17} /></span>}
              <div className="onboarding-card-icon"><BookOpenCheck size={27} /></div>
              <p className="onboarding-card-eyebrow">For learners</p>
              <h3 className="onboarding-card-title">I’m a Student</h3>
              <p className="onboarding-card-subtitle">
                Join tests, complete assessments, and view your feedback.
              </p>
              <ul className="onboarding-features">
                <li><CheckCircle2 size={15} />Join a test with a code</li>
                <li><CheckCircle2 size={15} />Complete assessments</li>
                <li><CheckCircle2 size={15} />View marks and feedback</li>
              </ul>
              <button className="onboarding-btn student-btn" type="button"
                onClick={() => handleSelect("student")} disabled={loading !== null}
                aria-label="Continue as student">
                {loading === "student"
                  ? <><LoaderCircle className="onboarding-spinner" size={18} aria-hidden="true" />Setting up...</>
                  : <>Continue as Student<ArrowRight size={17} /></>}
              </button>
            </article>
          </div>
        </section>

        <aside className="onboarding-reassurance">
          <ShieldCheck size={17} /><span>Not sure? You can contact support later if your account type needs changing.</span>
        </aside>
      </main>
    </div>
  );
};

export default OnboardingScreen;
